import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { approveDateRequest, declineDateRequest } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import { fmtDate, fmtRange } from '../projectSchedule.utils';

const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR } };

// One client request with Approve / Decline. Approving moves the visit to start on the requested date
// (keeping its length); only admins can answer, which the server also checks.
export function DateRequestActions({ request, canAnswer, onDone }) {
    const dispatch = useDispatch();
    const [busy, setBusy] = useState(null);
    const [declining, setDeclining] = useState(false);
    const [note, setNote] = useState('');
    const [error, setError] = useState(null);

    const answer = (approve) => {
        setBusy(approve ? 'approve' : 'decline'); setError(null);
        dispatch((approve ? approveDateRequest : declineDateRequest)(request.id, note.trim())).then((res) => {
            setBusy(null);
            if (res?.data?.success) onDone(res.data.message);
            else setError(res?.data?.message || 'Could not answer the request');
        });
    };

    return (
        <Box>
            <Typography sx={{ fontSize: '13.5px' }}>
                {fmtRange(request.original_start_date, request.original_end_date, 'ddd D MMM')} → <b>{fmtDate(request.requested_date, 'dddd D MMM')}</b>
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>
                Asked by {request.requested_by_name || 'the client'}{request.reason ? ` · “${request.reason}”` : ''}
            </Typography>
            {canAnswer ? (
                <>
                    {declining && (
                        <TextField fullWidth size="small" label="Reason for declining (shown to the client)" value={note} onChange={e => setNote(e.target.value)}
                            inputProps={{ maxLength: 1000 }} sx={{ ...fieldSx, mt: 1.25 }} />
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                        {!declining && (
                            <Button size="small" variant="contained" sx={buttonStyles.contained} disabled={!!busy} onClick={() => answer(true)}>
                                {busy === 'approve' ? <CircularProgress size={16} color="inherit" /> : 'Approve & move visit'}
                            </Button>
                        )}
                        <Button size="small" variant="outlined" sx={buttonStyles.outlined} disabled={!!busy} onClick={() => (declining ? answer(false) : setDeclining(true))}>
                            {busy === 'decline' ? <CircularProgress size={16} sx={{ color: THEME_COLOR }} /> : declining ? 'Send decline' : 'Decline'}
                        </Button>
                        {declining && <Button size="small" sx={{ color: '#6b7785', textTransform: 'none' }} onClick={() => { setDeclining(false); setNote(''); }}>Cancel</Button>}
                    </Box>
                </>
            ) : (
                <Typography sx={{ fontSize: '12px', color: '#6b7785', mt: 0.75 }}>An admin needs to approve or decline this.</Typography>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
        </Box>
    );
}

// Pending client date change requests, shown above the schedule
export default function DateRequestsBox({ requests, siteNames, canAnswer, onOpenTask, onDone }) {
    if (!requests.length) return null;
    return (
        <Box sx={{ border: '1px solid #f5d9a8', bgcolor: '#fffaf0', borderRadius: 3, p: 1.5, mt: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '13.5px' }}>📅 Date change requests from clients ({requests.length})</Typography>
            {requests.map(r => (
                <Box key={r.id} sx={{ borderTop: '1px solid #f3e3c3', pt: 1.25, mt: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '13.5px' }}>{r.title || 'Visit'}</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7785', flex: 1 }}>{siteNames[r.facility_id] || ''}</Typography>
                        {r.sub_task_id && <Button size="small" sx={{ color: THEME_COLOR, textTransform: 'none' }} onClick={() => onOpenTask(r)}>Open →</Button>}
                    </Box>
                    <DateRequestActions request={r} canAnswer={canAnswer} onDone={onDone} />
                </Box>
            ))}
        </Box>
    );
}
